/// <amd-module name="VSSUI/Components/Hub/Hub" />

import { IBreadCrumbData } from "OfficeFabric/Breadcrumb";
import { Fabric } from "OfficeFabric/Fabric";
import { BaseComponent } from "OfficeFabric/Utilities";
import * as React from "react";
import { IObservableArray, ObservableArray, ObservableLike } from "VSS/Core/Observable";
import { Splitter } from "VSSUI/Splitter";
import { css } from "VSSUI/Util";
import { childrenOfType } from "VSSUI/Utilities/ChildrenUtils";
import { HubViewOptionKeys } from "VSSUI/Utilities/HubViewState";
import { IViewOptionsValues, VIEW_OPTIONS_CHANGE_EVENT } from "VSSUI/Utilities/ViewOptions";
import * as VSSUIResources from "VSSUI/VSS.Resources.VSSUI";
import { IHub, IHubProps, ScrollableRegion } from "../../Components/Hub/Hub.Props";
import { HubHeader, IHubHeaderProps } from "../../HubHeader";
import {
    IOnOffViewActionProps,
    IPivotBar,
    IPivotBarProps,
    IPivotBarViewAction,
    PivotBar,
    PivotBarFocusItem,
    PivotBarViewActionArea,
    PivotBarViewActionType
} from "../../PivotBar";

import "VSS/LoaderPlugins/Css!VSSUI/Components/Hub/Hub";
import { PivotBarSize } from "../PivotBar";

/** @deprecated */
export interface IHubState {
    selectedPivot?: string;
    viewActions?: IPivotBarViewAction[] | IObservableArray<IPivotBarViewAction>;
    inFullScreenMode?: boolean;
    canBreadcrumbShrink?: boolean;
    maxBreadcrumbWidth?: number;
}

/** @deprecated */
export class Hub extends BaseComponent<IHubProps, IHubState> implements IHub {

    protected static defaultProps = {
        scrollableRegion: ScrollableRegion.PivotBar
    };

    private _viewActionsObservable: IObservableArray<IPivotBarViewAction>;
    private _underlyingViewActionsObservable: IObservableArray<IPivotBarViewAction>;
    private _chromeless: boolean;
    private _prevBreadcrumbWidth: number;
    private _breadcrumbItemCount: number;
    private _exitingFullScreenMode: boolean;
    public pivotBar: IPivotBar;

    constructor(props: IHubProps) {
        super(props);

        // Do a one-time evaluation to see if the page was rendered in no-chrome mode
        // in which case we won't give the full screen options.
        this._chromeless = document.body.classList.contains("chromeless");

        this.state = {
            viewActions: this._getViewActions(props),
            selectedPivot: this.props.hubViewState.selectedPivot.value,
            inFullScreenMode: this.props.hubViewState.viewOptions.getViewOption(HubViewOptionKeys.fullScreen),
            canBreadcrumbShrink: true
        };
    }

    public focus(focusItem: PivotBarFocusItem): void {
        switch (focusItem) {
            case PivotBarFocusItem.commands:
                if (this.pivotBar) {
                    this.pivotBar.focus(focusItem);
                }
                break;
        }
    }

    /**
     * This gets executed when a container component re-renders PivotBar.
     * @param nextProps New props.
     */
    public componentWillReceiveProps(nextProps: IHubProps) {
        if (this.props.viewActions !== nextProps.viewActions) {
            this.setState({
                viewActions: this._getViewActions(nextProps)
            });
        }
    }

    public componentDidMount() {
        this.props.hubViewState.selectedPivot.subscribe(this._onSelectedPivotChanged);
        this._events.on(this.props.hubViewState.viewOptions, VIEW_OPTIONS_CHANGE_EVENT, this._onViewOptionChanged);
    }

    public componentDidUpdate() {
        if (this._exitingFullScreenMode) {
            // if user is exiting the fullscreen mode, take the focus back to the fullscreen button while preserving the tabIndex for currently selected pivot.
            // for more context: !534845.
            this._exitingFullScreenMode = false;
            this.focus(PivotBarFocusItem.commands);
        }
    }

    public componentWillUnmount() {
        this.props.hubViewState.selectedPivot.unsubscribe(this._onSelectedPivotChanged);
        this._events.off(this.props.hubViewState.viewOptions, VIEW_OPTIONS_CHANGE_EVENT, this._onViewOptionChanged);
        if (this._underlyingViewActionsObservable) {
            this._underlyingViewActionsObservable.unsubscribe(this._onViewActionObservableChanged);
        }
    }

    public render() {
        const children = React.Children.toArray(this.props.children as React.ReactChild[]);

        this.validateChildren(children);

        const {
            className,
            commands,
            hideHeader,
            hubViewState,
            onRenderFilterBar,
            pivotProviders,
            onBeforePivotChange,
            onPivotBarRenderComplete,
            showFilterBarInline,
            showPivots,
            useShadowStyle,
            pivotRenderingModeOptions,
            onRenderSecondaryContent,
            splitterProps,
            scrollableRegion,
            pivotHeaderAriaLabel,
            breadcrumbIsExpandable,
            pivotBarContentAriaLabel,
            inlineFilterAriaLabel
        } = this.props;
        const { selectedPivot, viewActions, inFullScreenMode, maxBreadcrumbWidth, canBreadcrumbShrink } = this.state;

        if (breadcrumbIsExpandable) {
            children[0] = this._wireDataDidRender(children[0]);
        }

        const pivotProps: IPivotBarProps = {
            onPivotClicked: this._onPivotClicked,
            onBeforePivotChange,
            commands,
            selectedPivot,
            viewOptions: hubViewState.viewOptions,
            filter: hubViewState.filter,
            pivotProviders: pivotProviders,
            showPivots: inFullScreenMode ? false : showPivots,
            onRenderComplete: onPivotBarRenderComplete,
            viewActions,
            onRenderFilterBar,
            showFilterBarInline,
            showFilterBarViewOptionKey: HubViewOptionKeys.showFilterBar,
            filterBarClassName: "vss-Hub--filterBar",
            pivotRenderingModeOptions,
            pivotBarContentClassName: this.props.pivotBarContentClassName,
            isPivotBarContentScrollable: scrollableRegion === ScrollableRegion.PivotBar,
            isCommandBarSticky: scrollableRegion === ScrollableRegion.Hub,
            pivotBarSize: PivotBarSize.Tall,
            headerAriaLabel: pivotHeaderAriaLabel,
            canBreadcrumbShrink: canBreadcrumbShrink,
            maxBreadcrumbWidth: maxBreadcrumbWidth,
            pivotBarHeaderClassName: breadcrumbIsExpandable && "vss-PivotBar--header-with-expandable-breadcrumb",
            pivotBarContentAriaLabel: pivotBarContentAriaLabel,
            inlineFilterAriaLabel: inlineFilterAriaLabel
        };

        const pivotComponent = (
            <PivotBar
                {...pivotProps}
                className={css(
                    "vss-HubPivotBar",
                    scrollableRegion === ScrollableRegion.Hub && "vss-HubPivotBar--scrollable",
                    useShadowStyle && "shadowStyle",
                    hideHeader && "hideHeader",
                    this.props.pivotBarClassName
                )}
                componentRef={pivotBar => {
                    this.pivotBar = pivotBar;
                }}
            >
                {children}
            </PivotBar>
        );
        let hubContent = pivotComponent;

        if (onRenderSecondaryContent) {
            hubContent = <Splitter {...splitterProps} onRenderFarElement={() => pivotComponent} onRenderNearElement={onRenderSecondaryContent} />;
        }

        return <Fabric className={css("vss-Hub", className)}>{hubContent}</Fabric>;
    }

    private validateChildren(children: React.ReactNode[]) {
        const hubHeader = childrenOfType(children, HubHeader)[0];
        if (!hubHeader || hubHeader.index !== 0) {
            throw new Error("Expected first child of Hub to be HubHeader.");
        }
        if (this.state.inFullScreenMode) {
            children[hubHeader.index] = React.cloneElement(children[hubHeader.index] as React.ReactElement<any>, {
                collapseBreadcrumb: true
            });
        }
    }

    private _wireDataDidRender = (child: React.ReactNode): React.ReactChild => {
        return React.cloneElement(child as React.ReactElement<any>, {
            breadcrumbDataDidRender: (renderedData: IBreadCrumbData, itemsWidth?: number) => {
                this._dataDidRender(renderedData, itemsWidth);

                // if the first child defines breadcrumbDataDidRender, call it, too.
                const headerPropsElement = child as React.ReactElement<IHubHeaderProps>;
                if (headerPropsElement && headerPropsElement.props && headerPropsElement.props.breadcrumbDataDidRender) {
                    headerPropsElement.props.breadcrumbDataDidRender(renderedData, itemsWidth);
                }
            }
        });
    };

    private _dataDidRender = (renderedData: IBreadCrumbData, itemsWidth?: number): void => {
        const { maxBreadcrumbWidth, canBreadcrumbShrink } = this.state;
        const { minDisplayedBreadcrumbItems = 0 } = this.props;

        if (canBreadcrumbShrink && renderedData.renderedItems.length <= minDisplayedBreadcrumbItems) {
            this.setState({ canBreadcrumbShrink: false });
        } else if (!canBreadcrumbShrink && renderedData.renderedItems.length > minDisplayedBreadcrumbItems) {
            this.setState({ canBreadcrumbShrink: true });
        }

        const itemCount = renderedData.renderedItems.length + renderedData.renderedOverflowItems.length;
        const shouldSetMaxBreadcrumbWidth = () => {
            if (renderedData.renderedOverflowItems.length !== 0
                || itemsWidth === undefined) {
                return false;
            }
            if (maxBreadcrumbWidth === undefined) {
                return true;
            }
            return renderedData.renderedItems.length === 1 && itemsWidth > maxBreadcrumbWidth;
        }
        if (this._breadcrumbItemCount && itemCount !== this._breadcrumbItemCount) {
            // Clear out the max width if the number of items changes.
            this.setState({ maxBreadcrumbWidth: undefined });
        } else if (shouldSetMaxBreadcrumbWidth()) {
            // ResizeGroup rate limits handling resize events and may miss one signaling it to expand if the max width is exact.
            // Adding 12px fixes this.
            this.setState({ maxBreadcrumbWidth: itemsWidth + 12 });
        }

        this._breadcrumbItemCount = itemCount;
    };

    private _getFullScreenViewAction(): IPivotBarViewAction {
        return {
            key: HubViewOptionKeys.fullScreen,
            important: true,
            viewActionRenderArea: PivotBarViewActionArea.farRight,
            actionType: PivotBarViewActionType.OnOff,
            actionProps: {
                showText: false,
                onAriaLabel: VSSUIResources.ExitFullScreenAriaLabel,
                offAriaLabel: VSSUIResources.EnterFullScreenAriaLabel,
                onIconProps: { iconName: "BackToWindow" },
                offIconProps: { iconName: "FullScreen" }
            } as IOnOffViewActionProps
        };
    }

    private _getViewActions(props: IHubProps): IPivotBarViewAction[] | IObservableArray<IPivotBarViewAction> {
        if (ObservableLike.isObservable(props.viewActions)) {
            if (!props.hideFullScreenToggle && !this._chromeless) {
                if (this._underlyingViewActionsObservable) {
                    this._underlyingViewActionsObservable.unsubscribe(this._onViewActionObservableChanged);
                }

                // Create a new observable that adds the full screen action
                const viewActionObservable = props.viewActions as IObservableArray<IPivotBarViewAction>;
                const newViewActions = new ObservableArray([...viewActionObservable.value, this._getFullScreenViewAction()]);

                this._underlyingViewActionsObservable = viewActionObservable;
                this._viewActionsObservable = newViewActions;

                viewActionObservable.subscribe(this._onViewActionObservableChanged);
                return newViewActions;
            }

            return props.viewActions;
        } else {
            const viewActions = props.viewActions || [];
            if (!props.hideFullScreenToggle && !this._chromeless) {
                (viewActions as IPivotBarViewAction[]).push(this._getFullScreenViewAction());
            }

            return viewActions;
        }
    }

    private _onViewActionObservableChanged = (): void => {
        this._viewActionsObservable.value = [...this._underlyingViewActionsObservable.value, this._getFullScreenViewAction()];
    };

    private _onViewOptionChanged(changedOptions: IViewOptionsValues) {
        // Handle full-screen mode changes
        if (HubViewOptionKeys.fullScreen in changedOptions) {
            const inFullScreenMode = changedOptions[HubViewOptionKeys.fullScreen] === true;
            if (!inFullScreenMode) {
                this._exitingFullScreenMode = true;
            }
            this.setState({ inFullScreenMode });
        }
    }

    private _onSelectedPivotChanged = (newPivotKey: string) => {
        this.setState({
            selectedPivot: newPivotKey
        });
    };

    private _onPivotClicked = (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, pivotKey: string): void => {
        if (pivotKey !== this.props.hubViewState.selectedPivot.value) {
            if (!this.props.hubViewState.canSwitchPivots(pivotKey)) {
                const oldValue = this.props.hubViewState.selectedPivot.value;
                this.pivotBar.selectPivot(oldValue);
            } else {
                this.props.hubViewState.selectedPivot.value = pivotKey;
            }
        }
    };
}
